import { useCallback, useEffect, useRef, useState } from 'react'
import { deleteMessage, missionMessages, sendMissionMessage } from '../api/endpoints'
import { Modal } from './Modal'
import type { Message, MessageScope, Participant } from '../api/types'

/**
 * Messaging from the TOC.
 *
 * Whoever is running the event works here rather than on a phone, and the mission channel is
 * mostly theirs: safety calls, holds, "cease fire". So this is the surface where being able to
 * reach a whole side, or one squad, without walking to them actually matters.
 *
 * Polled rather than pushed. The dashboard has no websocket at all — it already polls the live
 * picture on a short tick — and adding a socket for one panel would mean a second connection, a
 * second authentication path and a second thing to reconnect. A message arriving up to a couple of
 * seconds late is not what decides a game.
 */
const POLL_MS = 2500

/** Channels the organiser can always use. A direct thread is picked from the roster instead. */
const CHANNELS: { scope: MessageScope; label: string }[] = [
  { scope: 'MISSION', label: 'Mission' },
  { scope: 'FACTION', label: 'Faction' },
]

export function ChatPanel({
  missionId,
  roster,
  meId,
  isHost = false,
}: {
  missionId: string
  roster: Participant[]
  meId: string
  /** A host may remove anybody's message; everybody else only their own. */
  isHost?: boolean
}) {
  const [scope, setScope] = useState<MessageScope>('MISSION')
  const [withUser, setWithUser] = useState<Participant | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [removing, setRemoving] = useState<Message | null>(null)

  const bottom = useRef<HTMLDivElement | null>(null)

  const activeScope: MessageScope = withUser ? 'DIRECT' : scope

  const load = useCallback(async () => {
    const result = await missionMessages(missionId, activeScope, withUser?.userId)
    if (result.ok) {
      setMessages(result.value)
      setError(null)
    } else {
      // Shown, but the last good conversation is left on screen. Blanking it on one failed poll
      // would make a brief network blip look like everything having been deleted.
      setError(result.error.message)
    }
  }, [missionId, activeScope, withUser])

  useEffect(() => {
    void load()

    // A hidden tab is not polled: browsers throttle its timers anyway, and the requests buy
    // nothing nobody is looking at. The same rule the rest of the dashboard follows.
    const tick = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, POLL_MS)

    return () => window.clearInterval(tick)
  }, [load])

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [messages])

  async function send(event: React.FormEvent) {
    event.preventDefault()
    const body = draft.trim()
    if (!body || sending) return

    setSending(true)
    // Cleared immediately so the same text cannot be sent twice by an impatient second click.
    setDraft('')

    const result = await sendMissionMessage(missionId, {
      scope: activeScope,
      body,
      recipientId: withUser?.userId,
      clientMessageId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    })

    setSending(false)

    if (result.ok) {
      setMessages((current) => [...current, result.value])
      setError(null)
    } else {
      setError(result.error.message)
      // Put back, so nobody loses what they typed to a failed request.
      setDraft(body)
    }
  }

  const others = roster.filter((person) => person.userId !== meId)

  return (
    <div className="stack">
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {CHANNELS.map((channel) => (
          <button
            key={channel.scope}
            type="button"
            className={!withUser && scope === channel.scope ? '' : 'ghost'}
            onClick={() => {
              setWithUser(null)
              setScope(channel.scope)
            }}
          >
            {channel.label}
          </button>
        ))}

        <select
          value={withUser?.userId ?? ''}
          onChange={(event) => {
            const chosen = others.find((person) => person.userId === event.target.value)
            setWithUser(chosen ?? null)
          }}
        >
          <option value="">Direct…</option>
          {others.map((person) => (
            <option key={person.userId} value={person.userId}>
              {person.callsign}
            </option>
          ))}
        </select>
      </div>

      {activeScope === 'MISSION' && (
        <p className="muted" style={{ margin: 0, fontSize: '0.85em' }}>
          {/* Said every time. Otherwise somebody uses this to coordinate, and finds out
              afterwards who was reading it. */}
          Everyone in the event can read this, including the other side.
        </p>
      )}

      {error && <div className="banner danger">{error}</div>}

      <div
        style={{
          maxHeight: 320,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {messages.length === 0 && <p className="muted">Nothing here yet.</p>}

        {messages.map((message) => (
          <div key={message.id || message.clientMessageId} className="message-row">
            <div style={{ minWidth: 0 }}>
              <span className="muted" style={{ fontSize: '0.8em' }}>
                {new Date(message.sentAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}{' '}
                {message.senderCallsign}
              </span>
              <div style={{ overflowWrap: 'anywhere' }}>{message.body}</div>
            </div>
            {message.id && (isHost || message.senderId === meId) && (
              <button
                className="icon-button remove"
                aria-label={`Remove message from ${message.senderCallsign}`}
                title="Remove message"
                onClick={() => setRemoving(message)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" aria-hidden>
                  <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
                </svg>
              </button>
            )}
          </div>
        ))}
        <div ref={bottom} />
      </div>

      {removing && (
        <Modal title="Remove this message?" onClose={() => setRemoving(null)}>
          <div className="stack">
            <p style={{ margin: 0 }}>
              <strong>{removing.senderCallsign}:</strong> {removing.body}
            </p>
            <p className="tertiary" style={{ margin: 0 }}>
              It disappears from every phone that shows it.
            </p>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="secondary" onClick={() => setRemoving(null)}>
                Cancel
              </button>
              <button
                className="danger"
                onClick={async () => {
                  const target = removing
                  setRemoving(null)
                  const result = await deleteMessage(missionId, target.id)
                  if (result.ok) {
                    setMessages((current) => current.filter((m) => m.id !== target.id))
                  } else {
                    setError(result.error.message)
                  }
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </Modal>
      )}

      <form className="row" style={{ gap: 8 }} onSubmit={(event) => void send(event)}>
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={withUser ? `Message ${withUser.callsign}` : 'Message'}
          style={{ flex: 1 }}
        />
        <button type="submit" disabled={sending || draft.trim() === ''}>
          Send
        </button>
      </form>
    </div>
  )
}
